import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import type { Alerta, EstadoVivoVehiculo, IndicadoresFlota, MensajeStream, UnidadFlota } from '@tms/contratos';
import { api } from '../api';
import type { EstadoConexion } from '../api';
import { useToast } from './Toast';

/** Velocidad (km/h) por encima de la cual una unidad reportando se considera en ruta. */
const VELOCIDAD_EN_MARCHA = 3;
const RECARGA_PERIODICA_MS = 60_000;

interface Estado {
  unidades: UnidadFlota[];
  serv: IndicadoresFlota | null;
  alertas: Alerta[];
  cargando: boolean;
  error: string | null;
  conexion: EstadoConexion;
  ultimaActualizacion: number;
  /** Último mensaje `viaje` del stream, para que el detalle se recargue. */
  ultimoViaje: { viajeId: number; ts: number } | null;
}

type Accion =
  | { t: 'flota'; unidades: UnidadFlota[]; ind: IndicadoresFlota }
  | { t: 'alertas'; alertas: Alerta[] }
  | { t: 'error'; mensaje: string }
  | { t: 'posicion'; vivo: EstadoVivoVehiculo }
  | { t: 'alerta'; alerta: Alerta }
  | { t: 'viaje'; viajeId: number }
  | { t: 'conexion'; conexion: EstadoConexion };

const inicial: Estado = {
  unidades: [],
  serv: null,
  alertas: [],
  cargando: true,
  error: null,
  conexion: 'reconectando',
  ultimaActualizacion: 0,
  ultimoViaje: null,
};

function upsert(alertas: Alerta[], a: Alerta): Alerta[] {
  const i = alertas.findIndex((x) => x.id === a.id);
  const copia = i >= 0 ? alertas.map((x, k) => (k === i ? a : x)) : [a, ...alertas];
  return copia.sort((x, y) => y.inicio.localeCompare(x.inicio));
}

function reducir(s: Estado, a: Accion): Estado {
  switch (a.t) {
    case 'flota':
      return { ...s, unidades: a.unidades, serv: a.ind, cargando: false, error: null, ultimaActualizacion: Date.now() };
    case 'alertas':
      return { ...s, alertas: a.alertas };
    case 'error':
      return { ...s, cargando: false, error: a.mensaje };
    case 'posicion': {
      const unidades = s.unidades.map((u) => {
        if (u.vehiculoIdMicrotrack !== a.vivo.vehiculoIdMicrotrack) return u;
        // Una posición nueva indica que reporta: DETENIDA o EN_RUTA según la velocidad.
        // Las unidades sin actividad (sin viaje) conservan el estado que informó el servidor.
        const reporta = u.estado !== 'SIN_ACTIVIDAD';
        const estado = reporta ? ((a.vivo.velocidadKmh ?? 0) > VELOCIDAD_EN_MARCHA ? 'EN_RUTA' : 'DETENIDA') : u.estado;
        return { ...u, vivo: a.vivo, estado } satisfies UnidadFlota;
      });
      return { ...s, unidades, ultimaActualizacion: Date.now() };
    }
    case 'alerta':
      return { ...s, alertas: upsert(s.alertas, a.alerta), ultimaActualizacion: Date.now() };
    case 'viaje':
      return { ...s, ultimoViaje: { viajeId: a.viajeId, ts: Date.now() } };
    case 'conexion':
      return { ...s, conexion: a.conexion };
  }
}

interface DatosCtx {
  unidades: UnidadFlota[];
  indicadores: IndicadoresFlota | null;
  alertas: Alerta[];
  cargando: boolean;
  error: string | null;
  conexion: EstadoConexion;
  ultimaActualizacion: number;
  ultimoViaje: { viajeId: number; ts: number } | null;
  resolver: (a: Alerta) => Promise<void>;
  justificar: (a: Alerta, motivo: string) => Promise<void>;
  recargar: () => void;
}

const Ctx = createContext<DatosCtx | null>(null);

export function DatosProvider({ children }: { children: ReactNode }) {
  const [s, dispatch] = useReducer(reducir, inicial);
  const { avisar } = useToast();
  const temporizadorViaje = useRef<ReturnType<typeof setTimeout>>();

  const cargarFlota = useCallback(async () => {
    try {
      const r = await api.flotaEstado();
      dispatch({ t: 'flota', unidades: r.unidades, ind: r.indicadores });
    } catch (e) {
      dispatch({ t: 'error', mensaje: e instanceof Error ? e.message : 'No se pudo cargar el estado de la flota.' });
    }
  }, []);

  const cargarAlertas = useCallback(async () => {
    try {
      const r = await api.alertas({ tamanio: 500 });
      dispatch({ t: 'alertas', alertas: r.items });
    } catch (e) {
      console.warn('No se pudieron cargar las alertas', e);
    }
  }, []);

  const recargar = useCallback(() => {
    void cargarFlota();
    void cargarAlertas();
  }, [cargarFlota, cargarAlertas]);

  useEffect(() => {
    recargar();
    const periodico = setInterval(() => void cargarFlota(), RECARGA_PERIODICA_MS);

    const alMensaje = (m: MensajeStream) => {
      if (m.tipo === 'posicion') dispatch({ t: 'posicion', vivo: m.datos });
      else if (m.tipo === 'alerta') dispatch({ t: 'alerta', alerta: m.datos });
      else if (m.tipo === 'viaje') {
        dispatch({ t: 'viaje', viajeId: m.datos.viajeId });
        // Cambió un viaje: se relee la flota (con un pequeño retraso para agrupar mensajes).
        clearTimeout(temporizadorViaje.current);
        temporizadorViaje.current = setTimeout(() => void cargarFlota(), 1200);
      }
    };
    const cerrar = api.suscribir(alMensaje, (conexion) => dispatch({ t: 'conexion', conexion }));
    return () => {
      cerrar();
      clearInterval(periodico);
      clearTimeout(temporizadorViaje.current);
    };
  }, [recargar, cargarFlota]);

  const resolver = useCallback(
    async (a: Alerta) => {
      try {
        const r = await api.resolverAlerta(a.id);
        dispatch({ t: 'alerta', alerta: r });
        avisar(`Novedad${a.patente ? ` de ${a.patente}` : ''} resuelta`);
      } catch (e) {
        avisar(e instanceof Error ? e.message : 'No se pudo resolver la novedad.', 'error');
        void cargarAlertas();
      }
    },
    [avisar, cargarAlertas],
  );

  const justificar = useCallback(
    async (a: Alerta, motivo: string) => {
      const r = await api.justificarAlerta(a.id, motivo);
      dispatch({ t: 'alerta', alerta: r });
      avisar('Justificación registrada');
    },
    [avisar],
  );

  // Contadores de alertas abiertas y de indicadores derivados de los datos locales,
  // así se actualizan al instante al resolver o justificar.
  const { unidades, indicadores } = useMemo(() => {
    const abiertas = s.alertas.filter((a) => a.estado === 'ABIERTA');
    const porUnidad = new Map<string, number>();
    for (const a of abiertas) if (a.vehiculoIdMicrotrack) porUnidad.set(a.vehiculoIdMicrotrack, (porUnidad.get(a.vehiculoIdMicrotrack) ?? 0) + 1);
    const unidades = s.unidades.map((u) => ({ ...u, alertasAbiertas: porUnidad.get(u.vehiculoIdMicrotrack) ?? 0 }));
    const cuenta = (e: string) => unidades.filter((u) => u.estado === e).length;
    const indicadores: IndicadoresFlota | null = s.serv
      ? {
          ...s.serv,
          enRuta: cuenta('EN_RUTA'),
          detenidas: cuenta('DETENIDA'),
          sinReporte: cuenta('SIN_REPORTE'),
          sinActividad: cuenta('SIN_ACTIVIDAD'),
          alertasAbiertas: abiertas.length,
          alertasCriticas: abiertas.filter((a) => a.nivel === 'CRITICA').length,
        }
      : null;
    return { unidades, indicadores };
  }, [s.unidades, s.serv, s.alertas]);

  const valor = useMemo<DatosCtx>(
    () => ({
      unidades,
      indicadores,
      alertas: s.alertas,
      cargando: s.cargando,
      error: s.error,
      conexion: s.conexion,
      ultimaActualizacion: s.ultimaActualizacion,
      ultimoViaje: s.ultimoViaje,
      resolver,
      justificar,
      recargar,
    }),
    [unidades, indicadores, s.alertas, s.cargando, s.error, s.conexion, s.ultimaActualizacion, s.ultimoViaje, resolver, justificar, recargar],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useDatos(): DatosCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useDatos fuera de DatosProvider');
  return c;
}
